export const P5_SUPPORTED_LOCALES = ['en', 'zh-CN']
export const P5_DEFAULT_LOCALE = 'en'

export const P5_MESSAGES = {
  en: {
    nav: {
      label: 'P5 product page navigation',
      upgrade: 'New Upgrade',
      features: 'Core Functions',
      design: 'Exterior Design',
      services: 'Support Services',
      comparison: 'Spec Compare',
      requestDemo: 'Request demo',
      languageSwitcher: 'P5 page language',
      chinese: 'Switch P5 page to Simplified Chinese',
      english: 'Switch P5 page to English'
    },
    hero: {
      eyebrow: 'The Professional Flagship',
      title: 'Measure Your Potential'
    },
    upgrade: {
      eyebrow: 'Completely Upgraded',
      title: 'Three Industry-First Features',
      lower: {
        title: 'Lower-Body Power Assessment',
        headlines: [
          'Powered by the Sayers Equation',
          'Force Plate-Level Lower-Body Power Estimation'
        ],
        points: [
          'A 20MP AI vision system captures vertical jump height with precision.',
          'Using the NSCA-recognized Sayers Equation, P5 automatically estimates peak lower-body power.',
          'Results are compared with age- and gender-based reference standards to evaluate explosive performance.'
        ]
      },
      upper: {
        title: 'Upper-Body Power Assessment',
        headlines: ['Biomechanics-Based Algorithm for Accurate Power Analysis'],
        points: [
          'A standardized 10-second push-up test measures upper-body power using body weight and movement data.',
          'Age-adjusted algorithms compare results with population reference standards.',
          'Real-time AI motion tracking detects improper form and compensatory movements for more reliable results.'
        ]
      },
      grip: {
        title: 'Grip Strength Assessment',
        headlines: ['Integrated Electronic Grip Sensor'],
        points: [
          'High-precision grip sensors measure maximum grip strength in both hands.',
          'Evaluate upper-body muscle strength, support sarcopenia risk screening, and monitor left-right balance.',
          'Historical records help track long-term changes and support rehabilitation planning.'
        ]
      }
    },
    core: {
      eyebrow: 'Core Functions',
      title: 'Build a Complete Picture of Human Performance',
      cards: {
        bodyComposition: '8-Electrode Multi-Frequency BIA Body Composition Analysis',
        aiVision: '20MP AI Vision',
        posture: 'Multi-Dimensional Posture Analysis',
        agility: 'Agility Reaction Assessment',
        balance: 'Balance Performance Assessment',
        bloodPressure: 'BP & Heart Risk Assessment',
        vitalCapacity: 'Vital Capacity Evaluation'
      }
    },
    design: {
      eyebrow: 'Designed to Stand Out',
      title: 'A Refined Design That Redefines Professional Equipment',
      chassis: {
        title: 'Slim Vertical Design',
        description: 'A sleek, integrated vertical form with clean, refined lines. Free from unnecessary elements, it blends naturally into premium fitness, wellness, and rehabilitation spaces while projecting a confident professional presence.'
      },
      screen: {
        title: '27-inch 4K Touch Display',
        description: 'A large 27-inch 4K touchscreen presents reports and posture imaging with exceptional clarity. A fully touch-enabled workflow delivers a premium, laboratory-grade user experience.'
      },
      turntable: {
        title: 'Signature Footprint Electrode Platform',
        description: 'Inspired by the shape of human footprints, the electrode platform combines metallic finishes with elegant detailing, making every assessment feel intuitive and refined.'
      },
      contours: {
        title: 'Smooth Curved Design',
        description: 'Soft curves replace rigid industrial lines, creating a design that feels approachable, durable, and instantly recognizable.'
      }
    },
    services: {
      eyebrow: 'Support Services',
      title: 'Precise data collection is just the beginning',
      reports: {
        title: 'Medical-Grade Reports, Available Everywhere',
        points: [
          'Generate a standardized professional report instantly after every assessment.',
          'Print directly when needed, with multilingual templates designed for global users.'
        ]
      },
      trends: {
        title: 'Visual Progress Over Time',
        points: [
          'Track progress across multiple assessments with intuitive visual comparisons.',
          'Receive personalized exercise and nutrition recommendations based on long-term data.',
          'Manage member records efficiently to improve coaching and client engagement.'
        ]
      },
      platform: {
        title: 'One Platform for Complete Health Management',
        points: [
          'Synchronize data across multiple devices in real time.',
          'Manage member profiles with powerful organization tools.',
          'Cloud and local backup work together to keep every record secure.'
        ]
      }
    },
    comparison: {
      title: 'Find Your Analyzer',
      features: {
        bodyComposition: 'Body Composition Analysis',
        postureDimensions: 'Posture & Body Dimension Analysis',
        exerciseSafety: 'Exercise Safety Assessment',
        cardiorespiratory: 'Cardiorespiratory Assessment',
        agilityReaction: 'Agility & Reaction Assessment',
        balance: 'Balance Assessment',
        lowerBodyPower: 'Lower-Body Power Assessment',
        upperBodyPower: 'Upper-Body Power Assessment',
        gripStrength: 'Grip Strength Assessment',
        lungCapacity: 'Lung Capacity Assessment',
        shoulderRange: 'Shoulder Range of Motion Assessment'
      },
      specs: {
        frequencies: 'Frequencies',
        display: 'Display',
        workingDistance: 'Working Distance'
      },
      products: {
        p5: {
          frequencies: '20 kHz · 100 kHz · 250 kHz',
          display: '27-inch 4K Fully Laminated Display',
          workingDistance: '1 m'
        },
        p3: {
          frequencies: '20 kHz · 100 kHz · 250 kHz',
          display: '32-inch 2K Bonded Display',
          workingDistance: '1.5 m'
        },
        p2: {
          frequencies: '20 kHz · 100 kHz · 250 kHz',
          display: '21.5-inch 2K Bonded Display',
          workingDistance: '1.5 m'
        },
        p1: {
          frequencies: '20 kHz · 100 kHz · 250 kHz',
          display: '21.5-inch 2K Bonded Display',
          workingDistance: '1.5 m'
        },
        p0: {
          frequencies: '20 kHz · 100 kHz · 250 kHz',
          display: '10.1-inch 2K Bonded Display',
          workingDistance: '0.5 m'
        }
      }
    },
    subscribe: {
      title: 'Subscribe',
      description: 'Get the latest news from ANOVATOR',
      placeholder: 'Your email address',
      unavailable: 'Newsletter subscription is not currently available.'
    },
    disclaimer: {
      title: 'Disclaimer:',
      paragraphs: [
        'The product images and screen content shown on the page above are for illustrative purposes only. The actual product appearance (including but not limited to color, size, and design details) and displayed content (including but not limited to backgrounds, interfaces, and graphic effects) may vary; please refer to the actual product.',
        'All data presented on the page above represents theoretical values obtained by ANOVATOR or its partner laboratories through internal testing under specific conditions (please refer to individual specifications for details). Actual performance may vary due to individual product differences, software versions, usage conditions, and environmental factors; please refer to actual usage.',
        "To ensure the accuracy of product information, specifications, and features, ANOVATOR may adjust or revise the text and images on the page above in real-time to align with the product's actual performance, specifications, metrics, and components. Such modifications may be made without prior notice due to changes in product batches or supply conditions."
      ]
    },
    accessibility: {
      heroProductAlt: 'Anovator P5 professional body analyzer',
      upgradeControls: 'Upgrade feature controls',
      upgradePrevious: 'Show previous upgrade feature',
      upgradeNext: 'Show next upgrade feature',
      upgradeAlt: {
        lower: 'Lower extremity explosive power jump assessment',
        upper: 'Upper extremity explosive power push-up assessment',
        grip: 'Electronic grip strength assessment'
      },
      designAlt: {
        chassis: 'Anovator P5 sleek and slender chassis',
        screen: 'Anovator P5 27-inch 4K touch screen',
        turntable: 'Anovator P5 footprint electrode turntable',
        contours: 'Anovator P5 soft curved chassis contours'
      },
      serviceControls: 'Support service controls',
      servicePrevious: 'Show previous support service',
      serviceNext: 'Show next support service',
      serviceAlt: {
        reports: 'ANOVATOR professional assessment reports',
        trends: 'ANOVATOR visualized trend analysis',
        platformDesktop: 'ANOVATOR health management platform dashboard',
        platformMobile: 'ANOVATOR health management platform mobile view'
      },
      comparisonProductAlt: '{model} body analyzer',
      comparisonChoose: 'Choose a P series comparison model',
      comparisonList: 'P series comparison models'
    },
    meta: {
      title: 'P5 - Anovator'
    }
  },
  'zh-CN': {
    nav: {
      label: 'P5 产品页面导航',
      upgrade: '全新升级',
      features: '核心功能',
      design: '外观设计',
      services: '配套服务',
      comparison: '参数比较',
      requestDemo: '申请演示',
      languageSwitcher: 'P5 页面语言',
      chinese: '将 P5 页面切换为简体中文',
      english: '将 P5 页面切换为英文'
    },
    hero: {
      eyebrow: '专业系列旗舰机',
      title: '量化潜能'
    },
    upgrade: {
      eyebrow: '全新升级',
      title: '三大全新功能业内首创',
      lower: {
        title: '下肢爆发力评估',
        headlines: [
          'Sayers 黄金公式，',
          '测力台级精准下肢功率测算'
        ],
        points: [
          '2000万像素AI视觉捕捉原地纵跳高度。',
          '搭载NSCA公认Sayers功率方程，自动计算下肢峰值功率。',
          '匹配年龄、性别五级权威体能常模，自动评估爆发力等级。'
        ]
      },
      upper: {
        title: '上肢爆发力评估',
        headlines: ['生物力学修正算法，多维量化上肢爆发潜能'],
        points: [
          '10秒标准俯卧撑测试，结合体重与运动位移计算峰值功率。',
          '内置年龄修正模型，并匹配五级大众体能常模。',
          'AI实时追踪动作质量，自动识别弯腰、塌腰及借力等代偿动作，让结果更加真实可靠。'
        ]
      },
      grip: {
        title: '握力评估',
        headlines: ['集成电子握力传感，检测双侧力量平衡'],
        points: [
          '搭配高精度电子握力传感器，精准测量双手最大握力。',
          '帮助评估上肢肌肉储备，辅助筛查肌少症风险。',
          '记录历史数据，持续追踪肌力变化，并提供康复训练建议。'
        ]
      }
    },
    core: {
      eyebrow: '核心功能',
      title: '更多维度构建完整运动表现画像',
      cards: {
        bodyComposition: '8电极多频段BIA人体成分检测',
        aiVision: '2000万像素AI视觉',
        posture: '多维体态捕捉',
        agility: '敏捷反应能力评估',
        balance: '平衡能力评估',
        bloodPressure: '血压与心率风险评估',
        vitalCapacity: '肺活量评估'
      }
    },
    design: {
      eyebrow: '外观设计',
      title: '焕新机身设计，打造视觉标杆',
      chassis: {
        title: '简约高挑机身',
        description: '修长立式一体化造型，线条干净利落，摒弃冗余结构，适配高端健身、康复各类场馆，一眼凸显专业科技气场。'
      },
      screen: {
        title: '27英寸4K触控大屏',
        description: '超大高清触控屏，让数据图表与体态影像更加细腻清晰。全流程触控交互，带来实验室级可视化测评体验。'
      },
      turntable: {
        title: '灵动足迹电极底座',
        description: '底座电极采用足迹造型设计。金属质感与光影细节相结合，让每一次检测都更具仪式感。'
      },
      contours: {
        title: '柔润曲面轮廓',
        description: '圆润厚实的机身轮廓，打破传统医疗设备的冰冷感。流畅的曲线兼顾安全、美观与品牌辨识度。'
      }
    },
    services: {
      eyebrow: '配套服务',
      title: '精准的数据采集只是一个开始',
      reports: {
        title: '医疗级测评报告多端同步',
        points: [
          '检测完成后，即可生成标准化专业报告。',
          '支持打印输出，多语言报告模板满足全球使用需求。'
        ]
      },
      trends: {
        title: '多期数据可视化对比分析',
        points: [
          '自动汇总历史检测数据，直观展示长期变化趋势。',
          '基于数据提供个性化运动与营养建议。',
          '完整管理会员档案，帮助提升私教服务效率与会员转化。'
        ]
      },
      platform: {
        title: '一站式健康管理平台',
        points: [
          '设备数据实时同步，统一管理所有检测信息。',
          '会员档案与标签分类更加高效。',
          '云端与本地双重备份，保障数据安全。'
        ]
      }
    },
    comparison: {
      title: '看看哪款体测仪适合你。',
      features: {
        bodyComposition: '人体成分检测',
        postureDimensions: '体态与维度测量',
        exerciseSafety: '运动安全评估',
        cardiorespiratory: '心肺功能评估',
        agilityReaction: '敏捷反应能力评估',
        balance: '平衡能力评估',
        lowerBodyPower: '下肢爆发力评估',
        upperBodyPower: '上肢爆发力评估',
        gripStrength: '握力评估',
        lungCapacity: '肺活量评估',
        shoulderRange: '肩关节活动度评估'
      },
      specs: {
        frequencies: '频段',
        display: '屏幕',
        workingDistance: '工作距离'
      },
      products: {
        p5: {
          frequencies: '20kHz；100kHz；250kHz',
          display: '27英寸4K全贴屏',
          workingDistance: '1 m'
        },
        p3: {
          frequencies: '20kHz；100kHz；250kHz',
          display: '32英寸2K框贴屏',
          workingDistance: '1.5 m'
        },
        p2: {
          frequencies: '20kHz；100kHz；250kHz',
          display: '21.5英寸2K框贴屏',
          workingDistance: '1.5 m'
        },
        p1: {
          frequencies: '20kHz；100kHz；250kHz',
          display: '21.5英寸2K框贴屏',
          workingDistance: '1.5 m'
        },
        p0: {
          frequencies: '20kHz；100kHz；250kHz',
          display: '10.1英寸2K框贴屏',
          workingDistance: '0.5 m'
        }
      }
    },
    subscribe: {
      title: '订阅',
      description: '获取ANOVATOR的最新资讯',
      placeholder: '你的邮箱地址',
      unavailable: '新闻资讯订阅功能当前暂不可用。'
    },
    disclaimer: {
      title: '特别声明：',
      paragraphs: [
        '以上页面中的产品图片及屏幕内容仅作示意，产品实际外观（包括但不限于颜色、尺寸、设计细节）与显示内容（包括但不限于背景、界面、图形效果）可能存在差异，一切请以实物为准。',
        '以上页面所呈现的全部数据，均为 ANOVATOR 或其合作实验室在特定测试条件下（详见各单项说明）通过内部测试得出的理论值。受产品个体差异、软件版本、使用条件及环境因素影响，实际使用效果可能有所不同，一切请以实际使用情况为准。',
        '为尽可能提供准确的产品信息、规格及功能，ANOVATOR 可能会对以上页面中的文字说明、图片等内容进行实时调整与修订，确保与产品实际性能、规格、指标及部件保持一致。因产品批次及供货条件变化，此类修改可能不会提前另行通知。'
      ]
    },
    accessibility: {
      heroProductAlt: 'Anovator P5 专业体测仪',
      upgradeControls: '全新升级功能切换控件',
      upgradePrevious: '显示上一项全新升级功能',
      upgradeNext: '显示下一项全新升级功能',
      upgradeAlt: {
        lower: '下肢爆发力纵跳评估',
        upper: '上肢爆发力俯卧撑评估',
        grip: '电子握力评估'
      },
      designAlt: {
        chassis: 'Anovator P5 简约高挑机身',
        screen: 'Anovator P5 27英寸4K触控大屏',
        turntable: 'Anovator P5 足迹电极底座',
        contours: 'Anovator P5 柔润曲面机身轮廓'
      },
      serviceControls: '配套服务切换控件',
      servicePrevious: '显示上一项配套服务',
      serviceNext: '显示下一项配套服务',
      serviceAlt: {
        reports: 'ANOVATOR 专业测评报告',
        trends: 'ANOVATOR 多期数据可视化趋势分析',
        platformDesktop: 'ANOVATOR 健康管理平台桌面端界面',
        platformMobile: 'ANOVATOR 健康管理平台移动端界面'
      },
      comparisonProductAlt: '{model} 体测仪',
      comparisonChoose: '选择用于对比的 P 系列型号',
      comparisonList: 'P 系列对比型号'
    },
    meta: {
      title: 'ANOVATOR P5 - 专业系列旗舰机'
    }
  }
}
